import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api-client';
import { useHeaderAction } from '@/lib/header-action-context';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Receipt, ChevronLeft, ChevronRight, DollarSign, Hash } from 'lucide-react';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassInput, listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';

interface LedgerEntry {
  id: string;
  metric: string;
  quantity: number;
  amount_cents: number;
  currency: string;
  billing_period: string;
  entry_type: string;
  created: string;
  application_id?: string;
}

const SORT_OPTIONS = [
  { key: 'date', label: 'Date' },
  { key: 'metric', label: 'Metric' },
  { key: 'amount', label: 'Amount' },
  { key: 'type', label: 'Type' },
];

const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'charge', label: 'Charge' },
  { value: 'refund', label: 'Refund' },
  { value: 'adjustment', label: 'Adjustment' },
] as const;

export default function CustomerLedgerPage() {
  const { setHeaderTitle } = useHeaderAction();
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [typeFilter, setTypeFilter] = useState('all');
  const limit = 20;

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/billing/ledger/customer?period=${period}&page=${page}&limit=${limit}`);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('CustomerLedgerPage:fetchLedger - Failed to fetch ledger', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLedger(); }, [period, page]);

  // Header icon + subtitle
  useEffect(() => {
    setHeaderTitle({
      icon: (
        <div
          className="flex h-7 w-7 items-center justify-center rounded-md text-teal-600"
          style={{ backgroundColor: 'color-mix(in srgb, #0d9488 30%, white)' }}
        >
          <Receipt size={16} />
        </div>
      ),
      title: 'Usage & Charges',
      subtitle: 'Review metered usage, charges, and adjustments by billing period.',
      intense: true,
    });
    return () => setHeaderTitle(null);
  }, [setHeaderTitle]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const totalAmount = entries.reduce((sum, e) => sum + e.amount_cents, 0);
  const formatCents = (c: number) => `$${(Math.abs(c) / 100).toFixed(2)}`;
  const totalPages = Math.ceil(total / limit);
  const filteredEntries = useMemo(() => {
    let result = entries;

    if (typeFilter !== 'all') {
      result = result.filter((entry) => entry.entry_type === typeFilter);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((entry) =>
        entry.metric.toLowerCase().includes(q) ||
        entry.entry_type.toLowerCase().includes(q) ||
        entry.billing_period.toLowerCase().includes(q),
      );
    }

    const sorted = [...result].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'metric':
          cmp = a.metric.localeCompare(b.metric);
          break;
        case 'amount':
          cmp = a.amount_cents - b.amount_cents;
          break;
        case 'type':
          cmp = a.entry_type.localeCompare(b.entry_type);
          break;
        case 'date':
        default:
          cmp = new Date(a.created).getTime() - new Date(b.created).getTime();
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [entries, searchQuery, typeFilter, sortField, sortDirection]);

  const entryTypeColor = (t: string) => {
    if (t === 'charge') return 'bg-slate-100 text-slate-800';
    if (t === 'refund') return 'bg-emerald-100 text-emerald-800';
    if (t === 'adjustment') return 'bg-amber-100 text-amber-800';
    return 'bg-slate-100 text-slate-800';
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-6">
      <div className="grid gap-4 md:grid-cols-3 px-6">
        <Card
          className={cn(
            'relative border-2 p-3 transition-all duration-200 min-h-[88px]',
            'border-l-4 border-slate-200 bg-white hover:shadow-md',
          )}
          style={{ borderLeftColor: '#0d9488' }}
        >
          <div className="flex flex-row items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-teal-100 text-teal-600">
              <DollarSign size={16} />
            </div>
            <div className="flex flex-col flex-1 min-w-0 gap-0.5">
              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                total charges
              </span>
              <span className="text-sm font-bold truncate text-slate-900">
                {formatCents(totalAmount)}
              </span>
              <span className="text-[10px] text-slate-500 leading-tight">
                {period}
              </span>
            </div>
          </div>
        </Card>
        <Card
          className={cn(
            'relative border-2 p-3 transition-all duration-200 min-h-[88px]',
            'border-l-4 border-slate-200 bg-white hover:shadow-md',
          )}
          style={{ borderLeftColor: '#0d9488' }}
        >
          <div className="flex flex-row items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-teal-100 text-teal-600">
              <Hash size={16} />
            </div>
            <div className="flex flex-col flex-1 min-w-0 gap-0.5">
              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                entries
              </span>
              <span className="text-sm font-bold truncate text-slate-900">
                {total}
              </span>
            </div>
          </div>
        </Card>
      </div>

      <CapabilityListPanel<LedgerEntry>
        items={filteredEntries.length > 0 ? [filteredEntries[0]] : []}
        sortOptions={SORT_OPTIONS}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search usage entries..."
        allTags={[]}
        selectedTags={new Set<string>()}
        onToggleTag={() => {}}
        onClearAllTags={() => {}}
        onSelectAllTags={() => {}}
        showTagsFilter={false}
        extraToolbar={
          <div className="flex items-center gap-2">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className={cn('w-44', listControlsGlassTrigger)}>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                {TYPE_FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="month"
              value={period}
              onChange={(e) => { setPeriod(e.target.value); setPage(1); }}
              className={cn('w-48', listControlsGlassInput)}
            />
          </div>
        }
        isLoading={loading}
        loadingMessage="Loading charges..."
        emptyStateVariant="glass"
        emptyMessage="No charges for this period."
        gridClassName="grid grid-cols-1"
        pagination={
          totalPages > 1 ? (
            <div className="flex items-center justify-between border-t px-4 py-3">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          ) : undefined
        }
        renderCard={() => (
          <Card key="ledger-table">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Metric</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Period</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="text-muted-foreground">{new Date(e.created).toLocaleDateString()}</TableCell>
                      <TableCell className="font-medium">{e.metric}</TableCell>
                      <TableCell>{e.quantity.toFixed(2)}</TableCell>
                      <TableCell className={e.amount_cents < 0 ? 'text-emerald-600' : ''}>{e.amount_cents < 0 ? '-' : ''}{formatCents(e.amount_cents)}</TableCell>
                      <TableCell><Badge className={entryTypeColor(e.entry_type)}>{e.entry_type}</Badge></TableCell>
                      <TableCell className="text-muted-foreground">{e.billing_period}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      />
    </div>
  );
}
